import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";

import { sendEventMetric } from "@expert/cloudwatch-metrics";

import type { SummarySource } from "./selectSummarySource";

import { getBackupGaiaSummary, getGaiaSummary } from "./sessionSummaryApiClient";
import { compareSummaries, reportSummaryComparison, type SummaryComparisonResult } from "./sessionSummaryShadow";
import { getSimplrSummary } from "./simplrSummaryApiClient";

export interface SessionSummaryRequest {
    /** Present for the primary summary path; absent falls back to GAIA session history. */
    callSid?: string;
    sessionId: string;
    partner: Partner;
}

export interface SessionSummaryConfig {
    /** Which backend serves the returned summary (see {@link selectSummarySource}). */
    source: SummarySource;
    /** When true, also fetch the Simplr summary in shadow and report the comparison (dark launch). */
    shadowEnabled: boolean;
    /** Invoked with the PII-safe comparison result for each shadow run that is not equal. */
    onComparison?: (result: SummaryComparisonResult) => void;
}

/**
 * Fetches the expert session summary, honoring the Simplr dark launch:
 *  - **live cutover** (`config.source === "simplr"`): returns the Simplr summary in place of GAIA,
 *    falling back to GAIA when Simplr returns nothing. Applies to the primary (`callSid`) path only.
 *  - **shadow** (`config.shadowEnabled`): returns the GAIA summary unchanged, and fetches the Simplr
 *    summary alongside it to compare/report. The shadow can never change or delay the returned value.
 */
export async function getSessionSummary(
    logger: Logger,
    request: SessionSummaryRequest,
    model: string,
    config: SessionSummaryConfig,
): Promise<string> {
    const { callSid, sessionId, partner } = request;

    if (config.source === "simplr" && callSid) {
        const simplrSummary = (await getSimplrSummary(logger, { callSid, sessionId, partner }, model)).trim();
        if (simplrSummary) {
            logger.info({ sessionId, partner }, "SessionSummary | Using Simplr summary (live cutover)");
            return simplrSummary;
        }
        logger.warn({ sessionId, partner }, "SessionSummary | Simplr live summary empty; falling back to GAIA");
        void sendEventMetric("sessionSummarySimplr.liveFallback");
    }

    const gaiaSummary = callSid
        ? await getGaiaSummary(logger, { callSid, sessionId, partner }, model)
        : await getBackupGaiaSummary(logger, { sessionId, partner }, model);

    if (config.shadowEnabled && callSid) {
        void runSummaryShadow(logger, { callSid, sessionId, partner }, model, gaiaSummary, config.onComparison);
    }

    return gaiaSummary;
}

async function runSummaryShadow(
    logger: Logger,
    request: { callSid: string; sessionId: string; partner: Partner },
    model: string,
    gaiaSummary: string,
    onComparison?: (result: SummaryComparisonResult) => void,
): Promise<void> {
    try {
        const start = performance.now();
        const simplrSummary = await getSimplrSummary(logger, request, model);
        const simplrLatencyMs = Math.round(performance.now() - start);
        reportSummaryComparison(compareSummaries(gaiaSummary, simplrSummary, simplrLatencyMs), logger, onComparison);
    } catch (error: unknown) {
        logger.warn({ error }, "GAIA/Simplr summary shadow | shadow fetch failed");
    }
}
